import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import { Steps, Modal, Form, Input, DatePicker, Row, Col, Layout, Divider,
  InputNumber, Select, Slider, Space } from 'antd';
import { addStage, updateStage, updateClientMeta } from '../../Store/slices/clientJourney.slice';
import JourneyPath from './JourneyPath';
import PageHeader from '../../Components/PageHeader';
import {StepForwardOutlined} from "@ant-design/icons";
import JourneyController from './JourneyController';
import {getSimulationV2} from "../../Api/timeseriesApi";
import {selectAllSimulations, selectLatestSimulation} from "../../Store/slices/largeDataAnalytics.slice";
import JourneySimulation from "./JourneySimulation";

const { Step } = Steps;
const { Option } = Select;

const preSelectedObjectives = [
  { label: 'Buy a House', shape: 'square', color: '#4998D5' },
  { label: 'Get Married', shape: 'heart', color: '#d90c69' },
  { label: 'Send a child to College', shape: 'plus', color: '#07b89b' },
  { label: 'Donation/Gift', shape: 'plus', color: '#193956' },
  { label: 'Retire', shape: 'flag', color: '#3c3f5b' },
];






const JourneyView = ({clientMeta, sortedStages, handleEditStage, setCurrentStage, annualReturn,
                       isModalVisible, handleOk, handleCancel, currentStage, customObjective,
                       handleObjectiveChange, maxWidth})=>{
  return (
      <div style={{ textAlign: 'center', margin: 'auto', maxWidth }}>
      <JourneyPath stages={sortedStages}
                   onEditStage={handleEditStage}
                   clientMeta={clientMeta}
                   maxWidth={maxWidth}
                   annualReturn={annualReturn}
      />
      <Modal
        title="Stage Details"
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <Form layout="vertical">
          <Form.Item label="Objective">
            <Select
              value={currentStage?.objective}
              onChange={handleObjectiveChange}
              placeholder="Select or create objective"
            >
              {preSelectedObjectives.map(obj => (
                <Option key={obj.label} value={obj.label}>{obj.label}</Option>
              ))}
              <Option value="custom">Custom Objective</Option>
            </Select>
          </Form.Item>
          {customObjective && (
            <>
              <Form.Item label="Custom Objective">
                <Input
                  value={currentStage?.objective}
                  onChange={(e) => setCurrentStage({ ...currentStage, objective: e.target.value })}
                  placeholder="Enter custom objective"
                />
              </Form.Item>
              <Form.Item label="Shape">
                <Select
                  value={currentStage?.shape}
                  onChange={(value) => setCurrentStage({ ...currentStage, shape: value })}
                >
                  <Option value="circle">Circle</Option>
                  <Option value="square">Square</Option>
                  <Option value="triangle">Triangle</Option>
                  <Option value="heart">Heart</Option>
                  <Option value="plus">Plus</Option>
                  <Option value="check">Check</Option>
                </Select>
              </Form.Item>
              <Form.Item label="Color">
                <Input
                  type="color"
                  value={currentStage?.color}
                  onChange={(e) => setCurrentStage({ ...currentStage, color: e.target.value })}
                />
              </Form.Item>
            </>
          )}
          <Form.Item label="Future Date">
            <DatePicker
              picker="year"
              value={currentStage?.date}
              onChange={(date) => setCurrentStage({ ...currentStage, date })}
            />
          </Form.Item>
         <Form.Item label="Withdrawal Amount">
            <InputNumber
              min={0}
              value={currentStage?.goal}
              onChange={(value) => setCurrentStage({ ...currentStage, goal: value })}
              formatter={(value) => `$ ${value}`}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

const ClientJourney = () => {
  const dispatch = useDispatch();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentStage, setCurrentStage] = useState(null);
  const [customObjective, setCustomObjective] = useState(false);
  const [annualReturn, setAnnualReturn] = useState(5); // Default annualized return
  const [maxWidth, setMaxWidth] = useState(1000);

  const { stages, clientMeta } = useSelector((state) => state.clientJourney);
  const allSimulations = useSelector(selectAllSimulations);
  const latestSimulation = allSimulations?.[allSimulations?.length - 1] ?? null;
  console.log(latestSimulation)

  console.log(latestSimulation)
  const sortedStages = useMemo(() => {
    return [...stages].sort((a, b) => a.date - b.date);
  }, [stages]);

  const runSimulation = useCallback((startStage, endStage) => {
    if (!startStage || !endStage) return;

    const startYear = startStage.date.year();
    const endYear = endStage.date.year();
    const years = endYear - startYear;

    dispatch(getSimulationV2({
      years,
      timeStepsPerYear: 252,
      initialValue: clientMeta.investments,
      nPaths: 10000,
      targetAnnualReturn: annualReturn / 100, // Convert percentage to decimal
      targetAnnualVolatility: 0.15, // You might want to make this configurable
      events: null,
      nSamples: 5,
      lowerPercentile: 30,
      upperPercentile: 70
    }));
  }, [annualReturn, clientMeta.investments, dispatch]);

  useEffect(() => {
    if (sortedStages.length >= 2) {
      runSimulation(sortedStages[0], sortedStages[sortedStages.length - 1]);
    }
  }, [sortedStages, runSimulation]);


  const handleAddStage = () => {
    setCurrentStage({ id: stages.length + 1, date: null, objective: '', goal: null, shape: 'circle', color: 'blue' });
    setCustomObjective(false);
    setIsModalVisible(true);
  };

  const handleEditStage = (stage) => {
    setCurrentStage(stage);
    setCustomObjective(!preSelectedObjectives.some(obj => obj.label === stage.objective));
    setIsModalVisible(true);
  };

  const handleOk = () => {
    if (currentStage.id > stages.length) {
      dispatch(addStage(currentStage));
    } else {
      dispatch(updateStage(currentStage));
    }
    setIsModalVisible(false);
  };



  const handleObjectiveChange = (value) => {
    const objective = preSelectedObjectives.find(obj => obj.label === value);
    if (objective) {
      setCurrentStage({
        ...currentStage,
        objective: objective.label,
        shape: objective.shape,
        color: objective.color,
      });
      setCustomObjective(false);
    } else {
      setCurrentStage({
        ...currentStage,
        objective: '',
        shape: 'circle',
        color: 'blue',
      });
      setCustomObjective(true);
    }
  };

  const handleClientMetaChange = (field, value) => {
    dispatch(updateClientMeta({ ...clientMeta, [field]: value }));
  };

  return (
      <Layout>
        <PageHeader title={'Client Journey'} />
        <Layout.Content style={{padding: '50px'}}>
          <Row wrap={false}>
            <Col flex={'0 0 300px'}>
                <JourneyController clientMeta={clientMeta}
                                   handleClientMetaChange={handleClientMetaChange}
                                   handleAddStage={handleAddStage}
                                   annualReturn={annualReturn} setAnnualReturn={setAnnualReturn}
                />
            </Col>
            <Col flex={'auto'} offset={1}>
              <JourneyView clientMeta={clientMeta} handleClientMetaChange={handleClientMetaChange}
                         sortedStages={sortedStages} handleEditStage={handleEditStage} setCurrentStage={setCurrentStage}
                         isModalVisible={isModalVisible} handleOk={handleOk} handleCancel={()=>setIsModalVisible(false)}
                         currentStage={currentStage} customObjective={customObjective} setCustomObjective={setCustomObjective}
                         handleObjectiveChange={handleObjectiveChange} maxWidth={maxWidth}
                           annualReturn={annualReturn}

              />
            </Col>
          </Row>
          <Row>
            {latestSimulation && <JourneySimulation simulation={latestSimulation} />}
          </Row>

        </Layout.Content>
      </Layout>
  );
};

export default ClientJourney;
